import firebase from "firebase/compat/app";

export interface UpdatableAppUserJSON {
  name: string;
  state_id: string;
  city_id: string;
}

export interface AppUserJSON extends UpdatableAppUserJSON {
  id: string;
  phone_number: string;
}

export interface UpdatableAppUserData {
  name: string | null;
  cityId: string | null;
  stateId: string | null;
}

export interface AppUserData extends UpdatableAppUserData {
  id: string;
  phoneNumber: string;
}

export class AppUser {
  private _id: string;
  private _phoneNumber: string;
  private _name: string | null;
  private _stateId: string | null;
  private _cityId: string | null;

  constructor({ id, name, phoneNumber, cityId, stateId }: AppUserData) {
    this._id = id;
    this._name = name;
    this._phoneNumber = phoneNumber;
    this._stateId = stateId;
    this._cityId = cityId;
  }

  get id(): string {
    return this._id;
  }

  get phoneNumber(): string {
    return this._phoneNumber;
  }

  get name(): string | null {
    return this._name;
  }

  get stateId(): string | null {
    return this._stateId;
  }

  get cityId(): string | null {
    return this._cityId;
  }

  get isComplete(): boolean {
    return (
      !!this._name && !!this._phoneNumber && !!this._stateId && !!this._cityId
    );
  }

  addUpdatableAppUserJSON(json: UpdatableAppUserJSON): void {
    this._name = json.name;
    this._stateId = json.state_id;
    this._cityId = json.city_id;
  }

  copy(): AppUser | null {
    return new AppUser(this);
  }

  toUpdatableAppUserJSON(): UpdatableAppUserJSON {
    if (!this.isComplete) {
      throw new Error(`Cannot update incomplete user: ${this}`);
    }

    return {
      name: `${this._name}`,
      state_id: `${this.stateId}`,
      city_id: `${this.cityId}`,
    };
  }

  static fromFirebaseUser(firebaseUser: firebase.User): AppUser {
    if (!firebaseUser.phoneNumber) {
      throw new Error(
        `Cannot create user without a phone number: ${JSON.stringify(
          firebaseUser
        )}`
      );
    }

    return new AppUser({
      id: firebaseUser.uid,
      name: null,
      phoneNumber: firebaseUser.phoneNumber,
      stateId: null,
      cityId: null,
    });
  }
}
